// place: put each slot's box in its spot on the page, and size it to fit.
// Boxes are <sl-slot> elements, never <div>, so the site's own "3rd div" counting doesn't shift.
// Several slots in one spot share an <sl-row>: side by side (wrapping on phones), or stacked.
// How wide a unit grows is its format's (render.js); here only the room around it: its margins, the
// side gap on an edge-to-edge page, and a grid cell it fills.

import { css, data, find } from './dom.js';
import { lum, rgba } from './theme.js';

/** @typedef {import('./config.js').Slot} Slot */

const px = (/** @type {string} */ v) => parseFloat(v) || 0;

/** The spot a slot goes in; slots with the same spot share a row. @param {Slot} s */
export const spot = s => `${s.position}|${s.selector}`;

/** @param {string} selector */
const all = selector => /** @type {NodeListOf<HTMLElement>} */ (document.querySelectorAll(selector));

/** @param {string} id */
export const boxOf = id => [...all('sl-slot')].find(b => b.dataset.sl === id);

/** @param {Element} anchor @param {Slot['position']} position @param {Element} box */
function put(anchor, position, box) {
    if (position === 'before') anchor.before(box);
    else if (position === 'after') anchor.after(box);
    else if (position === 'prepend') anchor.prepend(box);
    else anchor.append(box);
}

/**
 * The slot's box, created (or moved into its row) as needed. Null when this page doesn't have the spot.
 * @param {Slot} s
 * @param {boolean} pinned  the top bar lives on <body>
 * @param {boolean} shared  other slots use the same spot
 */
export function boxFor(s, pinned, shared) {
    let box = boxOf(s.id);
    const anchor = pinned ? document.body : find(s.selector);
    if (!box) {
        if (!anchor) return null;
        box = document.createElement('sl-slot');
        box.dataset.sl = s.id;
        if (s.format === 'bar') document.body.prepend(box);
        else if (pinned) document.body.append(box);
        else if (!shared) put(anchor, s.position, box);
    }
    /** @type {HTMLElement} */
    let outer = box;
    if (shared && anchor) {
        const key = spot(s);
        let row = [...all('sl-row')].find(r => r.dataset.key === key);
        if (!row) {
            row = document.createElement('sl-row');
            row.dataset.key = key;
            put(anchor, s.position, row);
        }
        if (box.parentElement !== row) row.append(box);
        // the row's layout first (stacked or side by side, and the side gap, once for the whole row):
        // the size of each slot inside depends on it
        row.toggleAttribute('data-stack', !!s.style?.stack);
        css(row, { ...fullRow(row), ...size(row) });
        outer = row;
    }
    // the site's own layout spaces a slot in a flex or grid layout or a list; a pinned one takes no room.
    // In a grid cell it fills the cell, whatever the site's place-items says
    const f = pinned ? '1' : flat(outer.parentElement);
    data(outer, 'flat', f);
    css(outer, { 'justify-self': f === 'cell' ? 'stretch' : '', 'align-self': f === 'cell' ? 'stretch' : '' });
    return box;
}

/**
 * A parent that spaces its children itself, where our margins would only add up: 'cell' for a grid
 * (or a row of cards that wraps), whose cell the slot fills; '1' for a list or a flex row; '' for none.
 * @param {Element | null} p
 */
const flat = p => {
    if (!p || /^(UL|OL)$/.test(p.tagName)) return '1';
    const s = getComputedStyle(p);
    if (/grid/.test(s.display)) return 'cell';
    if (!/flex/.test(s.display)) return '';
    // a flex column with no gap stacks like plain blocks, so it keeps our margin (it would sit flush otherwise)
    if (/column/.test(s.flexDirection)) return parseFloat(s.rowGap) > 0 ? '1' : '';
    return s.flexWrap === 'wrap' ? 'cell' : '1';
};

/**
 * Rows keep only spots with 2 or more slots, in the slots' order; a lone slot goes back to being a
 * normal slot.
 * @param {Map<string, number>} spots  how many slots share each spot
 * @param {string[]} ids  the slots drawn, in order
 */
export function tidyRows(spots, ids) {
    for (const row of all('sl-row')) {
        const kids = /** @type {HTMLElement[]} */ ([...row.children]);
        if ((spots.get(row.dataset.key || '') || 0) < 2 || kids.length < 2) row.replaceWith(...kids);
        else if (kids.some((k, i) => i && ids.indexOf(k.dataset.sl || '') < ids.indexOf(kids[i - 1].dataset.sl || ''))) {
            row.append(...kids.sort((x, y) => ids.indexOf(x.dataset.sl || '') - ids.indexOf(y.dataset.sl || '')));   // a box drawn again joins at the end
        }
    }
}

/**
 * Size an in-page box: the height its format reserves while booked, the full row in a centring flex
 * column, and the side gap.
 * @param {HTMLElement} box
 * @param {Slot} s
 * @param {number} reserve  the format's reserved height in px (0: none)
 */
export function fit(box, s, reserve) {
    css(box, { 'min-height': s.sponsor && reserve ? reserve + 'px' : '', ...fullRow(box), ...size(box) });
}

/** @param {Element | null} p */
const column = p => !!p && /flex/.test(getComputedStyle(p).display) && /column/.test(getComputedStyle(p).flexDirection);
/** Items in a row or grid are spaced by the site; a flex column stacks like normal blocks. @param {Element} p */
const laidOut = p => /flex|grid|table/.test(getComputedStyle(p).display) && !column(p);
/** Centring flex columns (hero sections) would shrink us to our content: take the full row. @param {HTMLElement} e */
const fullRow = e => (column(e.parentElement) ? { width: '100%', 'box-sizing': 'border-box' } : { width: '', 'box-sizing': '' });

/**
 * A box the eye sees as an edge: clipped, bordered, shadowed, or a different background from its parent.
 * @param {Element} a
 */
export function visibleBox(a) {
    const cs = getComputedStyle(a);
    if (/hidden|clip/.test(cs.overflowY)) return true;
    if ((px(cs.borderBottomWidth) > 0 && cs.borderBottomStyle !== 'none') || cs.boxShadow !== 'none') return true;
    const bg = rgba(cs.backgroundColor);
    if (bg[3] > 0.5) {
        const behind = a.parentElement ? rgba(getComputedStyle(a.parentElement).backgroundColor) : bg;
        return behind[3] < 0.5 || Math.abs(lum(bg) - lum(behind)) > 0.03;
    }
    return cs.backgroundImage !== 'none';
}

/** The edges a slot keeps clear of: the screen, or the nearest visible box around it. @param {Element} p */
function edges(p) {
    for (let a = /** @type {Element | null} */ (p); a && a !== document.body && a !== document.documentElement; a = a.parentElement) {
        if (visibleBox(a)) {
            const r = a.getBoundingClientRect(), cs = getComputedStyle(a);
            return [Math.max(0, r.left + px(cs.borderLeftWidth)), Math.min(document.documentElement.clientWidth, r.right - px(cs.borderRightWidth))];
        }
    }
    return [0, document.documentElement.clientWidth];
}

/** The inner left and right of an element (inside its border and padding). @param {Element} p */
function inner(p) {
    const r = p.getBoundingClientRect(), cs = getComputedStyle(p);
    return [r.left + px(cs.borderLeftWidth) + px(cs.paddingLeft), r.right - px(cs.borderRightWidth) - px(cs.paddingRight)];
}

/**
 * Side gap: many sites pad their text, not their containers, so a slot would run edge to edge while
 * the text beside it is indented. With less than 12px to the nearest visible edge, keep 16px each side.
 * @param {HTMLElement} e
 */
function touchesEdge(e) {
    const p = e.parentElement;
    if (!p || laidOut(p)) return false;
    const [l, r] = inner(p), [el, er] = edges(p);
    return l - el < 12 || er - r < 12;
}

/** The side gap (16px each side) on an edge-to-edge page; nothing otherwise. @param {HTMLElement} e */
function size(e) {
    return touchesEdge(e) ? { 'max-width': 'calc(100% - 32px)', 'margin-left': 'auto', 'margin-right': 'auto' } : { 'max-width': '', 'margin-left': '', 'margin-right': '' };
}
